/* eslint-disable react-hooks/exhaustive-deps */
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "../../components/ui/use-toast";
import fetchData from "../../utils/api";
import { Config } from "../../utils/config";
import ComprehensiveRating from "./StarRating";

export default function OrderDetails({
  cartList,
  couponCode,
  couponData,
  couponDiscount,
  subtotal,
  taxLabel,
  total,
  restaurantDetail,
  deliveryFees,
  deliveryFeesTax,
  orderingMethod,
  serviceFeesLabel,
  totalTipAmount,
  tipTaxFees,
  authToken,
  roundOff,
}) {
  const { t } = useTranslation();

  const handleSubmitRatingAndFeedback = async ({ rating }) => {
    try {
      const response = await fetchData({
        url: "orders/rating",
        method: "POST",
        token: authToken,
        body: { order_id: cartList.id, rating },
      });
      if (response.status_code === 200) {
        toast({
          title: t("rating.toast_title"),
          description: t(response.message),
        });
      } else {
        toast({
          variant: "destructive",
          title: t("rating.toast_title"),
          description: t(response.message),
        });
      }
    } catch (error) {
      console.error("error", error);
    }
  };

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "quantity",
      header: t("cart.table.quantity"),
      cell: ({ row }) => {
        return (
          <div className="flex gap-10 capitalize items-center font-bold">
            {row.getValue("quantity")}
          </div>
        );
      },
    },
    {
      accessorKey: "name",
      header: t("cart.table.item"),
      cell: ({ row }) => {
        const rowValue = row.original;

        return (
          <div>
            <div className="flex gap-1 items-center font-bold">
              {row.getValue("name")}{" "}
              {rowValue.availability !== "in_stock" && (
                <Badge variant="destructive">{t("out_of_stock")}</Badge>
              )}
            </div>
            {rowValue.size !== "" && (
              <div className="flex space-y-1 items-center">{rowValue.size}</div>
            )}
            {rowValue.choices_and_addons.length > 0 && (
              <div className="grid gap-1 capitalize items-center">
                {rowValue.choices_and_addons.map((item, index) => (
                  <div key={index} className="flex items-center">
                    {item}
                  </div>
                ))}
              </div>
            )}
            {rowValue?.is_instructions === true &&
              rowValue?.instructions !== "" && (
                <div className="mt-2">
                  <div className="flex space-y-1 items-center font-bold">
                    {rowValue.instructions}
                  </div>
                </div>
              )}
          </div>
        );
      },
    },
    {
      accessorKey: "price",
      header: t("cart.table.price"),
      cell: ({ row }) => {
        const rowValue = row.original;

        return (
          <div>
            <div className="flex gap-10 capitalize items-center font-bold">
              {restaurantDetail?.currencyDetails?.symbol}{" "}
              {typeof rowValue.countTotal === "object"
                ? rowValue.cart_total.pricing[
                    cartList?.orderingMethod === "dine_in"
                      ? "in_restaurant"
                      : cartList?.orderingMethod
                  ].toFixed(2) ?? 0
                : rowValue.countTotal.toFixed(2) ?? 0}
            </div>
            {rowValue.size !== "" && (
              <div className="flex space-y-1 capitalize items-center">
                {restaurantDetail?.currencyDetails?.symbol}{" "}
                {
                  rowValue.cart_total.pricing[
                    cartList?.orderingMethod === "dine_in"
                      ? "in_restaurant"
                      : cartList?.orderingMethod
                  ]
                }
              </div>
            )}
            {rowValue.choices_and_addons.length > 0 && (
              <div className="grid gap-1 capitalize items-center">
                {rowValue.cart_total.choices_and_addons.map((item, index) => (
                  <div key={index} className="flex">
                    {restaurantDetail?.currencyDetails?.symbol}{" "}
                    {item.pricing[
                      cartList?.orderingMethod === "dine_in"
                        ? "in_restaurant"
                        : cartList?.orderingMethod
                    ].toFixed(2)}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "total",
      header: t("cart.table.total"),
      cell: ({ row }) => {
        const rowValue = row.original;

        return (
          <div>
            <div className="flex gap-10 capitalize items-center font-bold">
              {restaurantDetail?.currencyDetails?.symbol}{" "}
              {rowValue.countTotal[
                cartList?.orderingMethod?.ordering_method === "dine_in"
                  ? "in_restaurant"
                  : cartList?.orderingMethod?.ordering_method || "delivery"
              ]?.toFixed(2)}{" "}
            </div>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: cartList?.orderDetails ?? [],
    columns,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });
  return (
    <div className="space-y-4">
      <CardContent className="w-full space-y-4 ">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    {t("cart_empty")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <Separator />
          <div className="p-3">
            {couponCode && (
              <Card className="mb-6">
                <CardHeader className="relative">
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col">
                      <CardTitle className="text-left text-md">
                        {couponData?.name}
                      </CardTitle>
                      <p className="text-left text-sm">{couponData?.coupon}</p>
                    </div>
                    {couponDiscount && (
                      <CardTitle>
                        - {restaurantDetail?.currencyDetails?.symbol}{" "}
                        {couponDiscount}{" "}
                      </CardTitle>
                    )}
                  </div>
                </CardHeader>
                <Separator />
                <CardContent>
                  <div>
                    <Label className="font-bold">
                      {t("cart.add_coupon_code.condition")}
                    </Label>
                  </div>
                  <div>
                    <Label>{t("cart.add_coupon_code.only_once_in_cart")}</Label>
                  </div>
                  {couponData?.details?.is_min_order === true && (
                    <div>
                      <Label>{t("cart.add_coupon_code.sub_total")}</Label>
                      <Label>
                        {restaurantDetail?.currencyDetails?.symbol}{" "}
                        {Number(couponData?.details?.min_order).toFixed(2)}
                      </Label>
                    </div>
                  )}
                  {couponData?.details?.client_type !==
                    "any_client_new_or_returning" && (
                    <div>
                      <Label>{t("cart.add_coupon_code.client_type")}</Label>
                      <Label>
                        {couponData?.details?.client_type === "only_new_clients"
                          ? t("cart.add_coupon_code.new")
                          : t("cart.add_coupon_code.returning")}
                      </Label>
                    </div>
                  )}
                  {couponData?.details?.is_order_type !== "any_type" && (
                    <div>
                      <Label>{t("cart.add_coupon_code.order_type")}</Label>
                      <Label>
                        {Config.env.app.order_type_custom
                          .filter((item) =>
                            couponData?.details?.order_type.includes(item.value)
                          )
                          .map((item) => item.label)
                          .join(", ")}
                      </Label>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            <div className="space-y-2">
              <div className="flex justify-between">
                <Label>{t("cart.table.sub_total")}</Label>
                <Label>
                  {restaurantDetail?.currencyDetails?.symbol} {subtotal ?? 0}
                </Label>
              </div>

              {orderingMethod === "delivery" && (
                <div className="grid gap-2">
                  <div className="flex justify-between">
                    <Label>
                      {t("cart.table.delivery_fees").replace(
                        "{amount}",
                        restaurantDetail?.taxation?.tax_delivery_fee
                      )}
                    </Label>
                    <Label>
                      {restaurantDetail?.currencyDetails?.symbol}{" "}
                      {Number(deliveryFees).toFixed(2)}
                    </Label>
                  </div>
                  {/* <div className="flex justify-between">
                    <Label>
                      {t("cart.table.delivery_fees_tax")} (
                      {(deliveryFeesTax / deliveryFees) * 100}%)
                    </Label>
                    <Label>
                      {restaurantDetail?.currencyDetails?.symbol}{" "}
                      {deliveryFeesTax}
                    </Label>
                  </div> */}
                </div>
              )}

              <div className="flex justify-between">
                <div className="grid gap-2 justify-start">
                  {taxLabel?.map((item, index) => (
                    <Label key={index} className="text-left">
                      {`${restaurantDetail.taxation.name_of_tax} - ${item.name}`}{" "}
                      (
                      {cartList?.taxMethod ===
                      "menu_prices_already_include_taxes"
                        ? `${item.tax}% included`
                        : `${item.tax}%`}
                      )
                    </Label>
                  ))}

                  {serviceFeesLabel?.map((item, index) => (
                    <Label key={index} className="text-left">
                      {item.name} ({item.tax})
                    </Label>
                  ))}

                  {totalTipAmount > 0 && (
                    <Label className="text-left">{t("cart.table.tips")}</Label>
                  )}
                </div>
                <div className="grid gap-2 justify-end">
                  {taxLabel?.map((item, index) => (
                    <Label key={index} className="text-right">
                      {restaurantDetail?.currencyDetails?.symbol}{" "}
                      {item.totalTax.toFixed(2)}
                    </Label>
                  ))}

                  {serviceFeesLabel?.map((item, index) => (
                    <Label key={index} className="text-right">
                      {restaurantDetail?.currencyDetails?.symbol}{" "}
                      {item.totalTax.toFixed(2)}
                    </Label>
                  ))}

                  {totalTipAmount > 0 && (
                    <Label className="text-right">
                      {restaurantDetail?.currencyDetails?.symbol}{" "}
                      {totalTipAmount}
                    </Label>
                  )}
                </div>
              </div>

              <div className="flex justify-between">
                <Label>{t("cart.table.round_off")}</Label>
                <Label>
                  {restaurantDetail?.currencyDetails?.symbol} {roundOff ?? 0}
                </Label>
              </div>
              <div className="flex justify-between pt-1">
                <Label className="font-bold text-lg">
                  {t("cart.table.total")}
                </Label>
                <Label className="font-bold text-lg">
                  {restaurantDetail?.currencyDetails?.symbol} {total ?? 0}
                </Label>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
      {cartList?.orderingMethod === "in_restaurant" &&
        cartList?.contactDetails?.table_reservation
          ?.hold_table_reservation_time && (
          <Card>
            <CardContent>
              <Label className="font-semibold">
                {t("table_reservation.note").replace(
                  "{time}",
                  cartList?.contactDetails?.table_reservation
                    ?.hold_table_reservation_time
                )}
              </Label>
            </CardContent>
          </Card>
        )}

      {cartList.status !== "delivered" && (
        <Card>
          <CardContent>
            <Label className="font-semibold text-center">
              {t("order_history.did_you_know")}
            </Label>
          </CardContent>
        </Card>
      )}
      {cartList.status === "delivered" && (
        <ComprehensiveRating
          rating={cartList?.rating ?? 0}
          feedback={cartList?.feedback ?? ""}
          menuRating={cartList?.menuRating ?? []}
          orderItems={cartList?.orderDetails ?? []}
          orderingMethod={cartList?.orderingMethod}
          onSubmit={(ratingData) => {
            handleSubmitRatingAndFeedback({ rating: ratingData });

            // Handle the submission of all ratings
            // You can make your API call here
          }}
        />
      )}
    </div>
  );
}
